export type ContactPayload = {
  name: string
  email: string
  subject: string
  message: string
  phone: string
}

export const MAX_NAME = 100
export const MAX_EMAIL = 200
export const MAX_SUBJECT = 100
export const MAX_MESSAGE = 5000
export const MIN_MESSAGE = 10
export const MAX_PHONE = 30

// Wie een proefperiode, demo of het founding-aanbod aanvraagt, hoeft niets toe te
// lichten: naam en e-mail zijn genoeg om contact op te nemen.
const MESSAGE_OPTIONAL_SUBJECTS = ['trial', 'demo', 'founding']

export function isMessageRequired(subject: string): boolean {
  return !MESSAGE_OPTIONAL_SUBJECTS.includes(subject)
}

export function isValidPhone(phone: string): boolean {
  const digits = phone.replace(/\D/g, '')
  return /^\+?[\d\s()-]+$/.test(phone) && digits.length >= 8 && digits.length <= 15
}

export type ContactValidation = {
  payload?: ContactPayload
  errors: string[]
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** Validate and normalize the JSON body before any string methods are called. */
export function validateContactPayload(input: unknown): ContactValidation {
  if (!isRecord(input)) {
    return { errors: ['name', 'email', 'message'] }
  }

  const errors: string[] = []
  const name = input.name
  const email = input.email
  const subject = input.subject
  const message = input.message
  const phone = input.phone

  if (typeof name !== 'string' || !name.trim() || name.length > MAX_NAME) {
    errors.push('name')
  }

  const normalizedName = typeof name === 'string' ? name.trim() : ''
  const normalizedEmail = typeof email === 'string' ? email.trim() : ''
  if (
    typeof email !== 'string' ||
    !normalizedEmail ||
    email.length > MAX_EMAIL ||
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)
  ) {
    errors.push('email')
  }

  // Subject was historically optional and fell back to "other". Keep that
  // compatibility while rejecting a present value with the wrong type.
  const normalizedSubject = subject === undefined ? 'other' : typeof subject === 'string' ? subject.trim() : ''
  if (
    (subject !== undefined && typeof subject !== 'string') ||
    normalizedSubject.length > MAX_SUBJECT
  ) {
    errors.push('subject')
  }

  const normalizedMessage = typeof message === 'string' ? message.trim() : ''
  const minMessage = isMessageRequired(normalizedSubject || 'other') ? MIN_MESSAGE : 0
  if (
    typeof message !== 'string' ||
    normalizedMessage.length < minMessage ||
    message.length > MAX_MESSAGE
  ) {
    errors.push('message')
  }

  // Telefoon is altijd optioneel: wie zijn nummer achterlaat, bellen we; anders mailen we.
  // Een verplicht nummer bleek een drempel op het proefformulier. Ouder clients sturen het veld niet mee.
  const normalizedPhone = typeof phone === 'string' ? phone.trim() : ''
  if (
    (phone !== undefined && typeof phone !== 'string') ||
    normalizedPhone.length > MAX_PHONE ||
    (normalizedPhone !== '' && !isValidPhone(normalizedPhone))
  ) {
    errors.push('phone')
  }

  if (errors.length) return { errors }

  return {
    errors: [],
    payload: {
      name: normalizedName,
      email: normalizedEmail,
      subject: normalizedSubject || 'other',
      message: normalizedMessage,
      phone: normalizedPhone,
    },
  }
}
